import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Link,
  Grid,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
} from "@mui/material";
import Lottie from "react-lottie";
import resultAnimationData from "../components/animations/0.json";
import apis from "../utils/apis";
import { useSelector, useDispatch } from "react-redux";
import ResultCarousel from "../page/ResultPageCarousel";
import Header from "../components/Header";
import { setResumeCarousel } from "../redux/action/result";
import { fetchResumeQuestionJson } from "../components/stepper_form/stepper.helper";
import { setResumeQuestionJson } from "../redux/action/resumeCarouselActions";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import ReactGA4 from "react-ga4";
import { setUserStatus } from "../redux/action/user";
import { setLoading } from "../redux/action/loadingRequest";
import { useNavigate } from "react-router-dom";


import Loading from "./common/Loading";

const UploadResume = ({
  secretEncodeDecodeString,
  staticRandomEncodeDecodeString,
}) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const ResumeAcceptedValidityPeriod = useFeatureFlag(
    "Resume_Accepted_Validity_Period"
  );
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null); // New state for warnings
  const [convertedMarketSalary, setConvertedMarketSalary] = useState(null);
  const [convertedTechValue, setConvertedTechvalue] = useState(null);
  const [uuid, setUuid] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0: Upload, 1: Form, 2: Result
  const [parsedResume, setParsedResume] = useState(null); // Parsed resume data
  const [formData, setFormData] = useState({}); // Form data from /get-updated-form
  const [formErrors, setFormErrors] = useState({}); // Track validation errors
  const [updatedData, setUpdatedData] = useState();
  const isMobile = useMediaQuery("(max-width:600px)");
  const shouldRunEffect = useRef(true);
  const [locationCorrect, setLocationCorrect] = useState(false);
  const isLoggedIn = useSelector(
    (state) => state.userReducer.isLoggedIn || false
  );

  const dispatch = useDispatch();
  const resultData = useSelector(
    (state) => state?.ResultReducer?.resumeCarousel || []
  );
  const resumeQuestionObject = useSelector(
    (state) =>
      state?.ResumeQuestionJsonReducer?.resumeQuestionObject?.questionList || []
  );
  const [resultTime, setResultTime] = useState(0);
  // Handlers
  const isFileRecent = (file) => {
    const currentDate = new Date(); // Current system date
    const monthsAgo = new Date(currentDate);

    // Use the harness flag value, defaulting to 6 if the flag is not available
    const validityPeriod = ResumeAcceptedValidityPeriod || 6;
    monthsAgo.setMonth(currentDate.getMonth() - validityPeriod);

    const fileModifiedDate = new Date(file.lastModified);
    return fileModifiedDate >= monthsAgo;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    dispatch(setResumeCarousel([]));
    setConvertedMarketSalary(null);
    setConvertedTechvalue(null);
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const validityPeriod = ResumeAcceptedValidityPeriod || 6;

      if (!["pdf"].includes(fileExtension)) {
        setError("Only .pdf files are allowed.");
        setWarning(null);
        setFile(null);
      } else if (fileSizeMB > 2) {
        setError("File size must be smaller than 2 MB.");
        setWarning(null);
        setFile(null);
      } else {
        setFile(selectedFile);
        setError(null);
        if (!isFileRecent(selectedFile)) {
          setWarning(
            `Warning: Your resume is older than ${validityPeriod} months. Results may not be accurate.`
          );
        } else {
          setWarning(null);
        }
      }
    }
  };

  // Handle drag-and-drop
  const handleDrop = (e) => {
    dispatch(setResumeCarousel([]));
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const validityPeriod = ResumeAcceptedValidityPeriod || 6;

      if (!["pdf"].includes(fileExtension)) {
        setError("Only .pdf files are allowed.");
        setWarning(null);
        setFile(null);
      } else if (fileSizeMB > 2) {
        setError("File size must be smaller than 2 MB.");
        setWarning(null);
        setFile(null);
      } else {
        setFile(selectedFile);
        setError(null);
        if (!isFileRecent(selectedFile)) {
          setWarning(
            `Warning: Your resume is older than ${validityPeriod} months. Results may not be accurate.`
          );
        } else {
          setWarning(null);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Encryption function
  function DataEncryption(json) {
    const jsonString = JSON.stringify(json);
    const customStringJson =
      secretEncodeDecodeString + jsonString + secretEncodeDecodeString;
    const newCustomStringJson = customStringJson.replace(
      /"/g,
      staticRandomEncodeDecodeString
    );
    const encodedString = btoa(newCustomStringJson);
    return encodedString;
  }

  // Decryption function
  function DataDecryption(jsonString) {
    try {
      const decodedString = atob(jsonString);
      const getOldStringOne = decodedString.replace(
        new RegExp(secretEncodeDecodeString, "g"),
        ""
      );
      const str = staticRandomEncodeDecodeString;
      const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(escapedString, "g");
      const getOldStringTwo = getOldStringOne.replace(regex, '"');
      return JSON.parse(getOldStringTwo);
    } catch (error) {
      throw new Error("Decryption failed. Invalid response structure.");
    }
  }

  // Fetch skill validity
  const fetchSkillValidity = async (skill) => {
    try {
      const encryptedPayload = DataEncryption({ primarySkill: skill });
      const response = await apis.POST({
        endpoint: "/get-topSkillValidity",
        payload: encryptedPayload,
        options: { type: "json" },
      });
      if (!response.data)
        throw new Error("No data received from skill validity API.");
      const decryptedData = DataDecryption(response.data);
      return decryptedData;
    } catch (error) {
      throw new Error(
        "We couldn't validate your skills due to a server issue. Please try again later."
      );
    }
  };

  // Handle resume upload and parsing
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file.");
      return;
    }

    setIsUploading(true);
    setError(null);
    setUuid(null);
    setConvertedMarketSalary(null);
    setConvertedTechvalue(null);

    const convertExperienceToMonths = (experience) => {
      if (!experience) return "12";
      const regex = /(\d+)\s*(\+?\s*years?)/i;
      const match = experience.match(regex);
      if (match) return String(parseInt(match[1], 10) * 12);
      const moreThanRegex = /more than\s+(\d+)\s*years?/i;
      const moreThanMatch = experience.match(moreThanRegex);
      if (moreThanMatch) return String(parseInt(moreThanMatch[1], 10) * 12);
      return "12";
    };

    const processParsedData = async (resumeData) => {
      try {
        if (
          !resumeData.name ||
          !resumeData.email ||
          !resumeData.primarySkill ||
          resumeData.primarySkill.length < 1
        ) {
          throw new Error(
            "Parsed resume does not contain required information"
          );
        }

        if (resumeData.experience) {
          resumeData.experience = convertExperienceToMonths(
            resumeData.experience
          );
          resumeData.payloadType = "carousel-resume";
        }

        return resumeData;
      } catch (error) {
        throw new Error(
          "We couldn't process your resume. Please ensure it contains the required information and try again."
        );
      }
    };

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const fileContent = reader.result.split(",")[1];
        let response;
        const maxRetries = 2;
        let retryCount = 0;

        // Retry logic for /get-parse-resume
        while (retryCount <= maxRetries) {
          try {
            response = await apis.POST({
              endpoint: "/get-parse-resume",
              payload: { fileName: file.name, fileContent },
              options: { type: "json" },
            });

            if (response.status === 200 || response.status === 201) {
              break; // Success, exit retry loop
            } else {
              throw new Error("Unexpected status code");
            }
          } catch (error) {
            const status = error?.response?.status || error?.status;
            if (status === 504 && retryCount < maxRetries) {
              retryCount++;
              const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
              console.log(
                `Parse attempt ${retryCount} failed with 504. Retrying in ${delay}ms...`
              );
              await new Promise((resolve) => setTimeout(resolve, delay));
            } else {
              throw error; // Throw if not 504 or max retries reached
            }
          }
        }

        if (!response.data) {
          throw new Error("No data received from the server.");
        }
        if (response.data.error) {
          throw new Error("Resume parsing failed");
        }

        const decryptionData = DataDecryption(response.data);
        if (!decryptionData || !decryptionData.body) {
          throw new Error("Decryption failed. Invalid response structure.");
        }

        const parsedData = JSON.parse(decryptionData.body);
        if (!parsedData.openAiResponse) {
          throw new Error("openAiResponse missing.");
        }

        const AIResponse = JSON.parse(parsedData.openAiResponse);
        let resumeData = await processParsedData(AIResponse);

        const processSkills = async (skillType, skillData) => {
          if (Array.isArray(skillData) && skillData.length > 0) {
            try {
              const validatedSkill = await fetchSkillValidity(skillData);
              if (validatedSkill && validatedSkill.topPrimarySkill) {
                resumeData[skillType] = validatedSkill.topPrimarySkill;
              } else {
                console.warn(`Invalid response for ${skillType} validity.`);
              }
            } catch (error) {
              console.error(
                `Error fetching ${skillType} validity:`,
                error.message
              );
            }
          } else {
            console.warn(`${skillType} is not a valid array or is empty.`);
          }
        };

        // Validate and process primarySkill and secondarySkill
        await processSkills("primarySkill", resumeData.primarySkill);
        await processSkills("secondarySkill", resumeData.secondarySkill);

        resumeData.payloadType = "carousel-resume";
        resumeData.fileUrl = parsedData.fileUrl;
        setParsedResume(resumeData);

        let updatedUserFormData;
        retryCount = 0;

        // Retry logic for /get-updated-form
        while (retryCount <= maxRetries) {
          try {
            updatedUserFormData = await apis.POST({
              endpoint: "/get-updated-form",
              payload: resumeData,
              options: { type: "json" },
            });

            if (
              updatedUserFormData.status === 200 ||
              updatedUserFormData.status === 201
            ) {
              break; // Success, exit retry loop
            } else {
              throw new Error("Unexpected status code");
            }
          } catch (error) {
            const status = error?.response?.status || error?.status;
            if (status === 504 && retryCount < maxRetries) {
              retryCount++;
              const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
              console.log(
                `Form update attempt ${retryCount} failed with 504. Retrying in ${delay}ms...`
              );
              await new Promise((resolve) => setTimeout(resolve, delay));
            } else {
              throw error; // Throw if not 504 or max retries reached
            }
          }
        }

        if (!updatedUserFormData.data) {
          throw new Error("No data received from updated form API.");
        }
        const updatedFormData = updatedUserFormData.data.userForm;

        const initialFormData = {};
        resumeQuestionObject.forEach((question) => {
          initialFormData[question.questionName] =
            question.questionName === "location" &&
            updatedFormData["suggestedLocation"]
              ? null
              : updatedFormData[question.questionName] ||
                resumeData[question.questionName] ||
                "";
        });
        setUpdatedData(updatedFormData);
        setFormData(initialFormData);
        setCurrentStep(1);
      } catch (error) {
        let errorMessage;
        switch (error.message) {
          case "No data received from skill validity API.":
            errorMessage =
              "We couldn't validate your skills. Please try again later.";
            break;
          case "We couldn't validate your skills due to a server issue. Please try again later.":
            errorMessage =
              "We couldn't validate your skills. Please try again later.";
            break;
          case "No data received from the server.":
            errorMessage =
              "No response from the server. Please try again later.";
            break;
          case "Resume parsing failed":
            errorMessage =
              "Resume parsing service is unavailable. Please try again later.";
            break;
          case "Parsed resume does not contain required information":
            errorMessage =
              "Your resume is missing key details (name, email, or skills). Please upload a complete resume.";
            break;
          case "Decryption failed. Invalid response structure.":
            errorMessage =
              "We couldn't process your resume data. Please try again later.";
            break;
          case "openAiResponse missing.":
            errorMessage = "Invalid resume data received. Please try again.";
            break;
          case "We couldn't process your resume. Please ensure it contains the required information and try again.":
            errorMessage =
              "We couldn't process your resume because it’s missing required information like name, email, or primary skills. Please check your resume and try again.";
            break;
          case "Failed to fetch updated form data.":
            errorMessage =
              "Unable to update your resume data. Our servers are currently busy. Please try again later.";
            break;
          case "No data received from updated form API.":
            errorMessage = "Failed to load form data. Please try again.";
            break;
          default:
            errorMessage =
              "Our servers are currently busy processing requests. Please try again in a few minutes.";
        }
        setError(errorMessage);
      } finally {
        setIsUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  // Convert months to years and months string Helper functions
  const convertMonthsToYearsAndMonths = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths) || totalMonths < 0) return "";
    if (totalMonths === 0) return "Fresher/Intern/Trainee";
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    let result = "";
    if (years > 0) {
      result += `${years} ${years > 1 ? "years" : "year"}`;
    }
    if (remainingMonths > 0) {
      result += `${years > 0 ? " " : ""}${remainingMonths} ${
        remainingMonths > 1 ? "months" : "month"
      }`;
    }
    return result.trim() || "0 months";
  };

  // Optional: Add an emoji based on experience level
  const getExperienceEmoji = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths)) return "";
    if (totalMonths === 0) return "🎓 ";
    const years = Math.floor(totalMonths / 12);
    if (years >= 5) return "🌟 ";
    if (years >= 2) return "🔥 ";
    if (years > 0) return "👍 ";
    const remainingMonths = totalMonths % 12;
    if (remainingMonths >= 6) return "✨ ";
    if (remainingMonths > 0) return "😊 ";
    return "";
  };

  // Validate a single field
  const validateField = (questionName, value) => {
    const question = resumeQuestionObject.find(
      (q) => q.questionName === questionName
    );
    if (!question) return;

    const errors = { ...formErrors };
    if (question.rules.required && (!value || value === "" || value === null)) {
      errors[questionName] = `${question.label} is required`;
    } else if (
      question.questionName === "experience" &&
      value &&
      (isNaN(value) || parseInt(value, 10) > 492)
    ) {
      errors[questionName] = isNaN(value)
        ? "Please enter a valid number"
        : "Experience cannot exceed 492 months (41 years)";
    } else {
      delete errors[questionName];
    }
    setFormErrors(errors);
  };

  // Validate entire form on submit
  const validateForm = () => {
    const errors = {};
    let allRequiredFilled = true;

    resumeQuestionObject.forEach((question) => {
      const value = formData[question.questionName];
      if (
        question.rules.required &&
        (!value || value === "" || value === null)
      ) {
        errors[question.questionName] = `${question.label} is required`;
        allRequiredFilled = false;
      } else if (
        question.questionName === "experience" &&
        value &&
        (isNaN(value) || parseInt(value, 10) > 492)
      ) {
        errors[question.questionName] = isNaN(value)
          ? "Please enter a valid number"
          : "Experience cannot exceed 492 months (41 years)";
      }
    });

    setFormErrors(errors);
    setError(allRequiredFilled ? null : "Please fill all required fields.");
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsFetchingResult(true);
    setError(null);
    try {
      const submittedData = { ...updatedData, ...formData };
      let resultResponse;
      const maxRetries = 2;
      let retryCount = 0;

      // Retry logic for /get-result
      while (retryCount <= maxRetries) {
        try {
          resultResponse = await apis.POST({
            endpoint: "/get-result",
            payload: submittedData,
            options: { type: "json" },
          });

          if (resultResponse.status === 200 || resultResponse.status === 201) {
            break; // Success, exit retry loop
          } else {
            throw new Error("Unexpected status code");
          }
        } catch (error) {
          const status = error?.response?.status || error?.status;
          if (status === 504 && retryCount < maxRetries) {
            retryCount++;
            const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
            console.log(
              `Result attempt ${retryCount} failed with 504. Retrying in ${delay}ms...`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            throw error; // Throw if not 504 or max retries reached
          }
        }
      }

      if (!resultResponse.data) {
        throw new Error("No result data received.");
      }
      if (resultResponse.data.error) {
        throw new Error("Salary calculation failed");
      }

      const resultData = resultResponse.data;
      if (!resultData.convertedMarketSalaryRange) {
        throw new Error("No salary data available");
      }

      if (resultData.uuid) {
        ReactGA4.event("resume_calculation_complete", {
          calculation_id: resultData.uuid,
          user_type: "candidate",
        });
      }

      dispatch(setResumeCarousel(submittedData));
      setUuid(resultData.uuid);
      dispatch(
        setUserStatus({
          isLoggedIn:
            sessionStorage.getItem("isLoggedIn") === "true" ? true : false,
          userRefId: sessionStorage.getItem("userRefId"),
          loading: false,
          uuid: resultData.uuid,
          // isReferralUsers: object?.data?.isReferralUsers,
        })
      );
      setResultTime(resultData.time);
      setConvertedMarketSalary(resultData.convertedMarketSalaryRange);
      setConvertedTechvalue(resultData.convertedTechValue || null);
      setCurrentStep(2);
    } catch (error) {
      let errorMessage;
      switch (error.message) {
        case "No result data received.":
          errorMessage = "No response from the server. Please try again later.";
          break;
        case "Salary calculation failed":
          errorMessage =
            "We couldn't calculate your market salary at this time. Please try again later.";
          break;
        case "No salary data available":
          errorMessage =
            "We don't have enough data to calculate your salary range. Please try again later.";
          break;
        default:
          errorMessage =
            "Our servers are currently busy processing your results. Please try again in a few minutes.";
      }
      setError(errorMessage);
    } finally {
      setIsFetchingResult(false);
    }
  };

  // Handle back to upload
  const handleBackToUpload = () => {
    setIsUploading(false);
    setCurrentStep(0);
    setFile(null);
    setError(null);
    setWarning(null);
    setConvertedMarketSalary(null);
    setConvertedTechvalue(null);
    setUuid(null);
    setParsedResume(null);
    setFormData({});
    setFormErrors({});
    dispatch(setResumeCarousel([]));
  };

  // Initial setup
  useEffect(() => {
    if (shouldRunEffect.current) {
      dispatch(setResumeCarousel([]));
      fetchResumeQuestionJson(dispatch, setResumeQuestionJson);
      shouldRunEffect.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn === false) {
      dispatch(setLoading(true));
    }
  }, []);

  // Get the current theme's text color and typography
  const currentThemeKey =
    useSelector((state) => state.themeReducer?.currentTheme) || 0;
  const themes = useSelector((state) => state.themeReducer?.themes);
  const currentTheme = themes[currentThemeKey];
  const currentFontKey = useSelector((state) => state.themeReducer.currentFont);
  const fonts = useSelector((state) => state.themeReducer.fonts);
  const currentFont = fonts[currentFontKey];

  return (
    <>
      {!isLoggedIn ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            backgroundImage:
              "url('../../assets/images/homepage-background.svg')",
          }}
          position="fixed"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#000",
            minHeight: "100vh",
            width: "100%",
            color: "#FFF",
            backgroundImage:
              "url('../../assets/images/homepage-background.svg')",
            backgroundRepeat: "repeat",
            backgroundSize: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Header />

          {/* Step 0: Upload Resume */}
          {currentStep === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 600,
                  margin: "auto",
                  padding: 3,
                }}
              >
                {!isUploading ? (
                  <Box>
                    <Paper
                      elevation={3}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      sx={{
                        padding: { xs: "20px", md: "30px" },
                        textAlign: "center",
                        border: "1px dashed #FDF9E0",
                        borderRadius: { xs: "10px", md: "36px" },
                        backgroundColor: "transparent",
                      }}
                    >
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Box
                          component="img"
                          sx={{
                            height: "100%",
                            width: "100%",
                            maxHeight: { xs: "80px", md: "146px" },
                            maxWidth: { xs: "80px", md: "168px" },
                          }}
                          alt="Upload Resume"
                          src="/assets/images/Upload-Resume.svg"
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontFamily: "Kanit",
                          marginBottom: "8px",
                          lineHeight: { xs: "26px", md: "58px" },
                          fontSize: { xs: "24px", md: "48px" },
                          color: "#FDF9E0",
                        }}
                      >
                        Upload Your Resume
                      </Typography>
                      {file ? (
                        <Typography
                          sx={{
                            fontWeight: 500,
                            marginBottom: "44px",
                            lineHeight: { xs: "100%", md: "34px" },
                            fontSize: { xs: "14px", md: "22px" },
                            color: "#FDF9E0",
                          }}
                        >
                          File Selected: {file.name}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 500,
                            marginBottom: "44px",
                            lineHeight: { xs: "100%", md: "34px" },
                            fontSize: { xs: "14px", md: "22px" },
                            color: "#FDF9E0",
                          }}
                        >
                          Drag, and Drop Files Here
                        </Typography>
                      )}
                      <Typography
                        component="label" // Ensures the text acts as a label for the file input
                        sx={{
                          fontWeight: 700,
                          lineHeight: "100%",
                          fontSize: { xs: "16px", md: "24px" },
                          cursor: isUploading ? "not-allowed" : "pointer", // Disable cursor if uploading
                          textDecoration: "underline",
                          color: "#FDF9E0",
                          opacity: isUploading ? 0.5 : 1, // Visual feedback when disabled
                        }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.docx"
                          onChange={handleFileChange}
                          disabled={isUploading} // Disable input when uploading
                        />
                      </Typography>
                    </Paper>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        marginTop: 2,
                      }}
                    >
                      {error && (
                        <Typography
                          color="error"
                          variant="body2"
                          sx={{ textAlign: "center" }}
                        >
                          {error}
                        </Typography>
                      )}
                      {warning && (
                        <Typography
                          variant="body2"
                          sx={{ textAlign: "center", color: "orange" }}
                        >
                          {warning}
                        </Typography>
                      )}
                      <Box sx={{ textAlign: "center" }}>
                        <Button
                          variant="contained"
                          onClick={handleUpload}
                          disabled={isUploading || !file}
                          sx={{
                            "&.Mui-disabled": {
                              backgroundColor: "#333",
                              color: "#000",
                            },
                            color: "#FDF9E0",
                            borderRadius: "8px",
                            mt: "30px",
                          }}
                        >
                          {isUploading ? "Processing..." : "Upload Resume"}
                        </Button>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          mt: "20px",
                          color: "#FDF9E0",
                        }}
                      >
                        Resume parsing may have errors. Retry or refine on{" "}
                        <Link
                          href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: "bold", cursor: "pointer" }}
                        >
                          TrueSelfy TechWorth.
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Loading />
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        mt: "10px",
                        color: "#FDF9E0",
                      }}
                    >
                      Resume parsing may have errors. Retry or refine on{" "}
                      <Link
                        href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontWeight: "bold", cursor: "pointer" }}
                      >
                        TrueSelfy TechWorth.
                      </Link>
                    </Typography>
                    <Paper
                      elevation={3}
                      sx={{
                        padding: 2,
                        mt: "20px",
                        width: "100%",
                        textAlign: "center",
                        border: "2px dashed #278C37",
                        borderRadius: "8px",
                        // backgroundColor: file ? "#278C37" : "#f9f9f9",
                        background: "rgba(39, 140, 55, 0.1)",
                      }}
                    >
                      {file && (
                        <Typography variant="body1" sx={{ color: "#FDF9E0" }}>
                          File Selected: {file.name}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  padding: "12px 20px",
                  backgroundColor: "#FDF9E0", // Light beige background
                  borderRadius: "10px",
                  display: "flex",
                  flexWrap: "wrap",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  mx: { xs: "24px", md: "7rem" },
                }}
              >
                <Typography
                  sx={{
                    color: "#000", // Black text
                    fontWeight: 700,
                    textAlign: { xs: "center", md: "unset" },
                    fontSize: { xs: "14px", md: "24px" },
                    flex: 1, // Allow it to take up space
                  }}
                >
                  Unleash your full value and showcase your individuality with
                  TechWorth!
                </Typography>

                <Button
                  sx={{
                    backgroundColor: "#000", // Black button
                    color: "#FFF", // White text
                    fontSize: { xs: "14px", md: "24px" },
                    fontWeight: 600,
                    padding: "8px 20px",
                    borderRadius: "8px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#333", // Slightly lighter on hover
                    },
                    mt: { xs: "8px", md: "0px" },
                  }}
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                >
                  Check TechWorth
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Form with Questions */}
          {currentStep === 1 && (
            <Box
              sx={{
                width: "100%",
                maxWidth: 600,
                margin: "auto",
                padding: 3,
              }}
            >
              {!isFetchingResult ? (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ textAlign: "center", pb: "5px", color: "#FDF9E0" }}
                  >
                    Review and Edit Your Information
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", pb: 2, color: "#FDF9E0" }}
                  >
                    Resume parsing may have errors. Retry or refine on{" "}
                    <Link
                      href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontWeight: "bold", cursor: "pointer" }}
                    >
                      TrueSelfy TechWorth.
                    </Link>
                  </Typography>
                  {resumeQuestionObject.length > 0 ? (
                    <Paper
                      elevation={3}
                      sx={{ backgroundColor: "transparent" }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          padding: 2,
                          textAlign: "center",
                          border: "2px dashed #278C37",
                          borderRadius: "8px",
                          // backgroundColor: file ? "#278C37" : "#f9f9f9",
                          background: "rgba(39, 140, 55, 0.1)",
                        }}
                      >
                        {file && (
                          <Typography variant="body1" sx={{ color: "#FDF9E0" }}>
                            File Selected: {file.name}
                          </Typography>
                        )}
                      </Paper>
                      {resumeQuestionObject.map((question) => (
                        <Box key={question.questionId} sx={{ marginTop: 2 }}>
                          {question.type === "number" ? (
                            <>
                              <TextField
                                fullWidth
                                type="number"
                                label={question.label}
                                value={formData[question.questionName] || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    [question.questionName]: value,
                                  }));
                                  validateField(question.questionName, value);
                                }}
                                helperText={
                                  formErrors[question.questionName] ||
                                  question.question_info
                                }
                                inputProps={{
                                  min: question.rules.min?.value || 0,
                                  max: question.rules.max?.value,
                                }}
                                required={question.rules.required}
                                error={!!formErrors[question.questionName]}
                                sx={{
                                  marginTop: 1,
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#231F20", // Background color
                                    "& fieldset": {
                                      borderColor: "#FDF9E0", // Default border color
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#FDF9E0", // Border color on hover
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#FDF9E0", // Border color when focused
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: "#FDF9E0", // Label color
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#FDF9E0", // Label color when focused
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "#FDF9E0", // Input text color
                                  },
                                  "& .MuiFormHelperText-root": {
                                    color: "#FDF9E0",
                                  },
                                }}
                              />
                              {question.questionName === "experience" &&
                                formData[question.questionName] !==
                                  undefined && (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#4caf50" }}
                                  >
                                    {getExperienceEmoji(
                                      formData[question.questionName]
                                    )}
                                    {convertMonthsToYearsAndMonths(
                                      formData[question.questionName]
                                    )}
                                  </Typography>
                                )}
                            </>
                          ) : (
                            <FormControl
                              fullWidth
                              error={!!formErrors[question.questionName]}
                            >
                              <Autocomplete
                                options={question.options}
                                value={
                                  question.questionName === "location" &&
                                  locationCorrect === false
                                    ? updatedData["suggestedLocation"]
                                      ? ""
                                      : formData["location"] || null
                                    : question.questionName === "role"
                                    ? question.options.find(
                                        (item) =>
                                          item === formData["role"] || null
                                      )
                                    : formData[question.questionName] || null
                                }
                                onChange={(event, newValue) => {
                                  setFormData({
                                    ...formData,
                                    [question.questionName]: newValue || "",
                                  });
                                  if (question.questionName === "location") {
                                    setLocationCorrect(true);
                                  }
                                  validateField(
                                    question.questionName,
                                    newValue || ""
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={question.label}
                                    required={question.rules.required}
                                    helperText={
                                      formErrors[question.questionName] ||
                                      question.question_info
                                    }
                                    error={!!formErrors[question.questionName]}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#231F20", // Background color
                                        "& fieldset": {
                                          borderColor: "#FDF9E0", // Default border color
                                        },
                                        "&:hover fieldset": {
                                          borderColor: "#FDF9E0", // Border color on hover
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#FDF9E0", // Border color when focused
                                        },
                                      },
                                      "& .MuiInputLabel-root": {
                                        color: "#FDF9E0", // Label color
                                      },
                                      "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#FDF9E0", // Label color when focused
                                      },
                                      "& .MuiInputBase-input": {
                                        color: "#FDF9E0", // Input text color
                                      },
                                      "& .MuiFormHelperText-root": {
                                        color: "#FDF9E0",
                                      },
                                    }}
                                  />
                                )}
                              />
                              {question.questionName === "location" && (
                                <Box
                                  sx={{
                                    marginTop: 1,
                                    paddingX: { xs: 1, sm: 0 }, // Add padding on mobile
                                  }}
                                >
                                  {updatedData["suggestedLocation"] && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        textAlign: "center",
                                        wordBreak: "break-word",
                                        color: "#FDF9E0",
                                        fontSize: {
                                          xs: "0.875rem",
                                          sm: "1rem",
                                        }, // Smaller font on mobile
                                        lineHeight: { xs: 1.2, sm: 1.5 }, // Tighter line height on mobile
                                      }}
                                    >
                                      We found your location{" "}
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontWeight: "bold",
                                          color: "#4caf50",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {updatedData["location"]}
                                      </Typography>
                                      {(!formData[question.questionName] ||
                                        !question.options.find(
                                          (option) =>
                                            option.name ===
                                            updatedData["suggestedLocation"]
                                        )) && (
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "block", // Put hint on new line
                                            mt: 0.5, // Add spacing above hint
                                            color: "#FDF9E0",
                                            fontSize: {
                                              xs: "0.75rem",
                                              sm: "0.875rem",
                                            },
                                          }}
                                        >
                                          - please select nearest location from
                                          above dropdown
                                        </Box>
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </FormControl>
                          )}
                        </Box>
                      ))}

                      {error && (
                        <Typography
                          color="error"
                          variant="body2"
                          sx={{ marginTop: 2, textAlign: "center" }}
                        >
                          {error}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          mt: 3,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleBackToUpload}
                          disabled={isFetchingResult}
                          sx={{
                            flex: 1,
                            borderColor: "#FDF9E0 ",
                            borderRadius: "8px",
                            color: "#FDF9E0",
                          }}
                        >
                          Back to Upload
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleFormSubmit}
                          disabled={
                            isFetchingResult ||
                            Object.keys(formErrors).length > 0
                          }
                          sx={{
                            flex: 2,
                            borderRadius: "8px",
                            color: "#FDF9E0",
                          }}
                        >
                          {isFetchingResult ? "Submitting..." : "Submit"}
                        </Button>
                      </Box>
                    </Paper>
                  ) : (
                    <Typography>No questions available.</Typography>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Lottie
                    options={{
                      animationData: resultAnimationData,
                      loop: true,
                      autoplay: true,
                    }}
                    height={300}
                    width={300}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "24px",
                      color: "#FDF9E0",
                    }}
                  >
                    Calculating your TechValue...
                  </Typography>
                  <Paper
                    elevation={3}
                    sx={{
                      padding: 2,
                      mt: "20px",
                      width: "100%",
                      textAlign: "center",
                      border: "2px dashed #278C37",
                      borderRadius: "8px",
                      // backgroundColor: file ? "#278C37" : "#f9f9f9",
                      background: "rgba(39, 140, 55, 0.1)",
                    }}
                  >
                    {file && (
                      <Typography variant="body1" sx={{ color: "#FDF9E0" }}>
                        File Selected: {file.name}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Step 2: Result Carousel */}
          {currentStep === 2 && (
            <Box>
              <ResultCarousel
                uuid={uuid}
                convertedMarketSalary={convertedMarketSalary}
                convertedTechValue={convertedTechValue}
                resultTime={resultTime}
              />
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBackToUpload}
                  sx={{
                    "&.Mui-disabled": {
                      backgroundColor: "#333",
                      color: "#000",
                    },
                    color: "#FDF9E0",
                    fontFamily: currentFont,
                    backgroundColor: currentTheme.primary,
                  }}
                >
                  Back to Upload
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default UploadResume;
